/**
 * TailwindUI Application UI / Elements / Buttons
 * https://tailwindui.com/components/application-ui/elements/buttons
 */
import React, { forwardRef } from 'react'
import ActivityIndicator from 'react-spinners/BeatLoader'
import { classNames } from '../../helpers'

export type ButtonProps = {
  id?: string
  text?: string
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  fullWidth?: boolean
  className?: string
  disabled?: boolean
  minWidth?: string
  type?: 'button' | 'submit' | 'reset'
  isLoading?: boolean
  activityIndicatorColor?: string
  icon?: React.ReactNode
  isPulsing?: boolean
  ariaLabel?: string
  ariaExpanded?: boolean
  ariaControls?: string
  autoFocus?: boolean
}

type InternalProps = ButtonProps & { customClassname: string }

const Base = forwardRef<HTMLButtonElement, InternalProps>(
  (
    {
      customClassname,
      id,
      text,
      onClick,
      fullWidth,
      className,
      disabled = false,
      minWidth = 'min-w-24',
      type = 'button',
      isLoading = false,
      activityIndicatorColor,
      icon,
      isPulsing,
      ariaLabel,
      ariaExpanded,
      ariaControls,
      autoFocus
    },
    ref
  ) => (
    <button
      ref={ref}
      id={id}
      autoFocus={autoFocus}
      data-autofocus={autoFocus || undefined}
      aria-label={ariaLabel || text}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      aria-busy={isLoading || undefined}
      aria-disabled={disabled || undefined}
      className={classNames(
        customClassname,
        fullWidth && 'w-full',
        disabled && 'opacity-40',
        'relative whitespace-nowrap transition',
        // Press-Feedback, bei reduzierter Bewegung aus
        !disabled && !isLoading && 'motion-safe:active:scale-[.98]',
        isLoading && 'cursor-default',
        minWidth,
        className,
        isPulsing && 'pulse-call-to-action'
      )}
      onClick={isLoading || disabled ? undefined : onClick}
      type={type}
      disabled={isLoading || disabled}
    >
      <span
        className={classNames(
          isLoading ? 'invisible' : 'visible',
          'flex flex-row items-center justify-center transition-all duration-300 ease-in-out'
        )}
      >
        {icon && <span className={text ? 'mr-1' : ''}>{icon}</span>}
        {text && (
          <span className='transition-all duration-300 ease-in-out'>
            {text}
          </span>
        )}
      </span>
      {isLoading && (
        <div
          className='absolute left-0 top-0 flex h-full w-full items-center justify-center'
          role='status'
          aria-live='polite'
        >
          <ActivityIndicator color={activityIndicatorColor || 'white'} />
        </div>
      )}
    </button>
  )
)

const make =
  (customClassname: string, activityIndicatorColor: string) =>
  (props: ButtonProps) => (
    <Base
      customClassname={customClassname}
      activityIndicatorColor={activityIndicatorColor}
      {...props}
    />
  )

const Text = make(
  'text-sm font-semibold text-[#1660F3] hover:text-primaryBlue/70',
  '#374151'
)

const PrimaryXS = make(
  'rounded-full bg-primaryBlue py-1 px-2.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600',
  'white'
)
const PrimarySM = make(
  'rounded-full bg-primaryBlue py-1 px-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600',
  'white'
)
const PrimaryBase = make(
  'rounded-full bg-primaryBlue py-1.5 px-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600',
  'white'
)
const PrimaryLG = make(
  'rounded-full bg-primaryBlue py-2 px-3.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600',
  'white'
)
const PrimaryXL = make(
  'rounded-full bg-primaryBlue py-2.5 px-4 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600',
  'white'
)

const SecondaryXS = make(
  'rounded-full bg-white py-1 px-2.5 text-xs font-semibold text-buttonSecondaryBlue shadow-sm ring-1 ring-inset ring-softGrey hover:bg-gray-50',
  '#374151'
)
const SecondarySM = make(
  'rounded-full bg-white py-1 px-2.5 text-sm font-semibold text-buttonSecondaryBlue shadow-sm ring-1 ring-inset ring-softGrey hover:bg-gray-50',
  '#374151'
)
const SecondaryBase = make(
  'rounded-full bg-white py-1.5 px-3 text-sm font-semibold text-buttonSecondaryBlue shadow-sm ring-1 ring-inset ring-softGrey hover:bg-gray-50',
  '#374151'
)
const SecondaryLG = make(
  'rounded-full bg-white py-2 px-3.5 text-sm font-semibold text-buttonSecondaryBlue shadow-sm ring-1 ring-inset ring-softGrey hover:bg-gray-50',
  '#374151'
)
const SecondaryXL = make(
  'rounded-full bg-white py-2.5 px-4 text-sm font-semibold text-buttonSecondaryBlue shadow-sm ring-1 ring-inset ring-softGrey hover:bg-gray-50',
  '#374151'
)

const SoftXS = make(
  'rounded-full bg-blue-50 py-1 px-2 text-xs font-semibold text-blue-600 shadow-sm hover:bg-blue-100',
  '#374151'
)
const SoftSM = make(
  'rounded-full bg-blue-50 py-1 px-2 text-sm font-semibold text-blue-600 shadow-sm hover:bg-blue-100',
  '#374151'
)
const SoftBase = make(
  'rounded-full bg-blue-50 py-1.5 px-2.5 text-sm font-semibold text-blue-600 shadow-sm hover:bg-blue-100',
  '#374151'
)
const SoftLG = make(
  'rounded-md bg-blue-50 py-2 px-3 text-sm font-semibold text-blue-600 shadow-sm hover:bg-blue-100',
  '#374151'
)
const SoftXL = make(
  'rounded-full bg-blue-50 py-2.5 px-3.5 text-sm font-semibold text-blue-600 shadow-sm hover:bg-blue-100',
  '#374151'
)

const WarningLG = make(
  'rounded-full bg-red-600 py-2 px-3.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600',
  'white'
)
const WarningText = make(
  'text-sm font-semibold text-red-500 hover:text-red-600',
  '#374151'
)

export default {
  Text,
  PrimaryXS,
  PrimarySM,
  PrimaryBase,
  PrimaryLG,
  PrimaryXL,
  SecondaryXS,
  SecondarySM,
  SecondaryBase,
  SecondaryLG,
  SecondaryXL,
  SoftXS,
  SoftSM,
  SoftBase,
  SoftLG,
  SoftXL,
  WarningLG,
  WarningText
}
